/* eslint-disable camelcase */
/** @typedef {import('../index.js').BaseDocData} BaseDocData */
import { Timestamp } from 'firebase/firestore';

/**
 * @typedef {Object} OrganizationSchema
 * @property {string} name - Name of organization.
 * @property {string} name_lowercase - Name of organization in lowercase.
 * @property {string} notes - Notes on the organization.
 * @property {string[]} domains - Domains associated with the organization.
 * @property {Timestamp} createDate - Date the organization was created as a Firestore Timestamp: {@link https://firebase.google.com/docs/reference/node/firebase.firestore.Timestamp}
 * @property {Timestamp} updateDate - Date the organization was last updated as a Firestore Timestamp: {@link https://firebase.google.com/docs/reference/node/firebase.firestore.Timestamp}
 *
 * @typedef {BaseDocData & OrganizationSchema} OrganizationDocData
*/

/**
 * Class representing an organization.
 * @class
 */
export default class Organization {
  /** @type {OrganizationDocData['validation']} */ validation = 'passed';
  /** Firestore document ID @type {OrganizationDocData['id']} */ id;
  /** @type {OrganizationDocData['name']} */ name;
  /** @type {OrganizationDocData['name_lowercase']} */ name_lowercase;
  /** @type {OrganizationDocData['notes']} */ notes;
  /** Array of associated domains @type {OrganizationDocData['domains']} */ domains;
  /** @type {OrganizationDocData['createDate']} */ createDate;
  /** @type {OrganizationDocData['updateDate']} */ updateDate;

  /**
   * Create an organization.
   * @param {OrganizationDocData} param0 - An {@link OrganizationDocData} object.
   */
  constructor({
    id, name, name_lowercase, notes, domains, createDate, updateDate,
  }) {
    this.id = id || null;
    this.name = name;
    this.name_lowercase = name_lowercase || (name ? name.trim().toLowerCase() : '');
    this.notes = notes || '';
    this.domains = domains || [];
    this.createDate = createDate;
    this.updateDate = updateDate;
  }

  /**
   * Converts the {@link Organization} instance to a Firestore data object.
   * @returns {Omit<OrganizationDocData, 'id'>} {@link OrganizationDocData} (without `id`).
   */
  toFirestore({ serverTimestamp }) {
    return {
      name: this.name,
      name_lowercase: this.name ? this.name.trim().toLowerCase() : '',
      notes: this.notes,
      domains: this.domains,
      createDate: this.createDate || serverTimestamp(),
      updateDate: serverTimestamp(),
    };
  }

  /**
   * Creates a {@link Organization} instance from Firestore data.
   * @param {any} docData - Raw Firestore document.
   * @returns A {@link Organization} instance.
   */
  static fromFirestore(docData) {
    return new Organization(docData);
  }
}
